import { arcTessellationAngle } from "./helpers";
/** Divide arc to at least the specified number of segments. */
const minArcTessellationSubdivisions = 32;
export function decomposePolyline(entity, dxfHeaders) {
    let entityVertices;
    let verticesCount;
    if (entity.includesCurveFitVertices || entity.includesSplineFitVertices) {
        entityVertices = entity.vertices.filter((v) => v.splineVertex || v.curveFittingVertex);
        verticesCount = entityVertices.length;
    }
    else {
        entityVertices = entity.vertices;
        verticesCount = entity.vertices.length;
    }
    if (verticesCount < 2) {
        return [];
    }
    entityVertices = MirrorEntityVertices(entity, entityVertices);
    const startIdx = 0;
    const curPlainLine = isPlainLine(entityVertices[0]);
    const curLineType = getLineType(entity, entityVertices[0], dxfHeaders);
    let curVertices = [];
    function CommitSegment(endIdx) {
        if (endIdx === startIdx) {
            return [];
        }
        let vertices = curVertices;
        if (endIdx === verticesCount && startIdx === 0) {
            if (!vertices) {
                vertices = entityVertices;
            }
        }
        else if (endIdx === verticesCount - 1 && startIdx === 0) {
            if (!vertices) {
                vertices = entityVertices;
            }
        }
        else if (endIdx === verticesCount) {
            if (!vertices) {
                vertices = entityVertices.slice(startIdx, endIdx);
                vertices.push(entityVertices[0]);
            }
        }
        else {
            if (!vertices) {
                vertices = entityVertices.slice(startIdx, endIdx + 1);
            }
        }
        // if is closed shape then make first vertice last to close shape
        if (entity.shape) {
            vertices.push(vertices[0]);
        }
        return vertices;
    }
    for (let vIdx = 1; vIdx <= verticesCount; vIdx++) {
        const prevVtx = entityVertices[vIdx - 1];
        let vtx;
        if (vIdx === verticesCount) {
            if (!entity.shape) {
                return CommitSegment(vIdx - 1);
            }
            vtx = entityVertices[0];
        }
        else {
            vtx = entityVertices[vIdx];
        }
        if (prevVtx.bulge !== undefined && curPlainLine) {
            if (!curVertices) {
                curVertices = entityVertices.slice(startIdx, vIdx);
            }
            generateBulgeVertices(curVertices, prevVtx, vtx, prevVtx.bulge);
        }
        else if (curVertices) {
            curVertices.push(vtx);
        }
        if (vIdx === verticesCount) {
            return CommitSegment(vIdx);
        }
        const isPlainLineVar = isPlainLine(vtx);
        const lineType = getLineType(entity, vtx, dxfHeaders);
        if (isPlainLineVar !== curPlainLine ||
            /* Line type is accounted for plain lines only. */
            (curPlainLine && lineType !== curLineType)) {
            return CommitSegment(vIdx);
        }
    }
    return [];
}
/** Check if start/end with are not specified. */
function isPlainLine(entity) {
    return !(entity.startWidth || entity.endWidth);
}
/**
 * @param entity
 * @param vertex
 * @param blockCtx {?BlockContext}
 * @return {number}
 */
function getLineType(entity, _vertex = undefined, _dxfHeaders) {
    //XXX lookup
    return 0;
}
/** Mirror entity vertices if necessary in case of extrusionDirection with negative Z specified.
 *
 * @param entity Entity to check.
 * @param vertices {?{x,y}[]} Vertices array to use instead of entity vertices attribute.
 * @return {{x,y}[]} Vertices array with mirrored X if necessary. All attributes preserved.
 */
function MirrorEntityVertices(entity, vertices = undefined) {
    if (!entity.extrusionDirection || entity.extrusionDirection.z >= 0) {
        return vertices ?? entity.vertices;
    }
    if (!vertices || vertices === entity.vertices) {
        vertices = entity.vertices.slice();
    }
    const n = vertices.length;
    for (let i = 0; i < n; i++) {
        const v = vertices[i];
        const _v = { x: -v.x, y: 0 };
        for (const propName in v) {
            if (!v.hasOwnProperty(propName)) {
                continue;
            }
            if (propName !== "x") {
                // @ts-ignore
                _v[propName] = v[propName];
            }
        }
        vertices[i] = _v;
    }
    return vertices;
}
/** Generate vertices for bulged line segment.
 *
 * @param vertices Generated vertices pushed here.
 * @param startVtx Starting vertex. Assuming it is already present in the vertices array.
 * @param endVtx Ending vertex.
 * @param bulge Bulge value (see DXF specification).
 */
function generateBulgeVertices(vertices, startVtx, endVtx, bulge) {
    const a = 4 * Math.atan(bulge);
    const aAbs = Math.abs(a);
    if (aAbs < arcTessellationAngle) {
        vertices.push(endVtx);
        return;
    }
    const ha = a / 2;
    const sha = Math.sin(ha);
    const cha = Math.cos(ha);
    const d = { x: endVtx.x - startVtx.x, y: endVtx.y - startVtx.y };
    const dSq = d.x * d.x + d.y * d.y;
    if (dSq < Number.MIN_VALUE * 2) {
        /* No vertex is pushed since end vertex is duplicate of start vertex. */
        return;
    }
    const D = Math.sqrt(dSq);
    let R = D / 2 / sha;
    d.x /= D;
    d.y /= D;
    const center = {
        x: (d.x * sha - d.y * cha) * R + startVtx.x,
        y: (d.x * cha + d.y * sha) * R + startVtx.y,
    };
    let numSegments = Math.floor(aAbs / arcTessellationAngle);
    if (numSegments < minArcTessellationSubdivisions) {
        numSegments = minArcTessellationSubdivisions;
    }
    if (numSegments > 1) {
        const startAngle = Math.atan2(startVtx.y - center.y, startVtx.x - center.x);
        const step = a / numSegments;
        if (a < 0) {
            R = -R;
        }
        for (let i = 1; i < numSegments; i++) {
            const a = startAngle + i * step;
            const v = {
                x: center.x + R * Math.cos(a),
                y: center.y + R * Math.sin(a),
            };
            vertices.push(v);
        }
    }
    vertices.push(endVtx);
}
//# sourceMappingURL=decomposePolyline.js.map